import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Cloud as CloudIcon
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';
import SettingsErrorAlert from './SettingsErrorAlert';

const TabPanel = ({ value, index, children }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

/** Multi-select for software apps + services on a repo or project row. */
const CatalogCategorySelect = ({
  applications,
  services,
  applicationIds = [],
  serviceIds = [],
  disabled,
  onChange
}) => {
  const appOptions = applications.map((a) => ({
    key: `app:${a.id}`,
    kind: 'application',
    id: a.id,
    label: `${a.short_name || a.name}${a.short_name && a.name !== a.short_name ? ` — ${a.name}` : ''} (app)`
  }));
  const svcOptions = services.map((s) => ({
    key: `svc:${s.id}`,
    kind: 'service',
    id: s.id,
    label: `${s.short_name || s.name}${s.short_name && s.name !== s.short_name ? ` — ${s.name}` : ''} (service)`
  }));
  const options = [...appOptions, ...svcOptions];
  const value = options.filter((o) =>
    (o.kind === 'application' && applicationIds.includes(o.id))
    || (o.kind === 'service' && serviceIds.includes(o.id))
  );

  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      options={options}
      value={value}
      disabled={disabled}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      onChange={(_, selected) => {
        onChange({
          application_ids: selected.filter((s) => s.kind === 'application').map((s) => s.id),
          service_ids: selected.filter((s) => s.kind === 'service').map((s) => s.id)
        });
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props} key={option.key}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />
          {option.label}
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder="Apps / services…" variant="outlined" />
      )}
      sx={{ minWidth: 220, maxWidth: 360 }}
    />
  );
};

const RepositorySettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cloudTab, setCloudTab] = useState(0);
  const [azureOrgs, setAzureOrgs] = useState([]);
  const [azureConnection, setAzureConnection] = useState({});
  const [gcp, setGcp] = useState({ enabled: false, message: 'Coming soon' });
  const [aws, setAws] = useState({ enabled: false, message: 'Coming soon' });

  const [addOrgOpen, setAddOrgOpen] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [addingOrg, setAddingOrg] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [credForm, setCredForm] = useState({
    provider: 'azure_devops',
    username: 'pat',
    organization: '',
    token: ''
  });
  const [gitCreds, setGitCreds] = useState({ token_set: false });
  const [savingCreds, setSavingCreds] = useState(false);
  const [applications, setApplications] = useState([]);
  const [services, setServices] = useState([]);
  const [savingCategoryKey, setSavingCategoryKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [repoRes, appsRes, svcRes] = await Promise.all([
        api.get('/settings/repositories'),
        api.get('/catalog/applications').catch(() => ({ data: { data: [] } })),
        api.get('/catalog/services').catch(() => ({ data: { data: [] } }))
      ]);
      const data = repoRes.data?.data || {};
      setAzureOrgs(data.azure?.organizations || []);
      setAzureConnection(data.azure?.connection || {});
      setGcp(data.gcp || { enabled: false, message: 'Coming soon' });
      setAws(data.aws || { enabled: false, message: 'Coming soon' });
      setApplications((appsRes.data?.data || []).filter((a) => a.is_active !== false));
      setServices((svcRes.data?.data || []).filter((s) => s.is_active !== false));
      if (data.git_credentials) {
        setGitCreds(data.git_credentials);
        setCredForm((f) => ({
          ...f,
          provider: data.git_credentials.provider || 'azure_devops',
          username: data.git_credentials.username || 'pat',
          organization: data.git_credentials.organization || '',
          token: ''
        }));
      }
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load repository settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddOrganization = async () => {
    const organization = orgName.trim();
    if (!organization) {
      toast.error('Enter Azure DevOps organization name or URL');
      return;
    }
    setAddingOrg(true);
    try {
      const response = await api.post('/settings/repositories/azure/organizations', { organization });
      toast.success(response.data?.message || 'Organization connected');
      setAddOrgOpen(false);
      setOrgName('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add organization');
    } finally {
      setAddingOrg(false);
    }
  };

  const handleSync = async (org) => {
    setSyncingId(org.id);
    try {
      const response = await api.post(`/settings/repositories/azure/organizations/${org.id}/sync`);
      toast.success(response.data?.message || 'Synced');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sync failed');
      await load();
    } finally {
      setSyncingId(null);
    }
  };

  const handleCategoryChange = async (org, itemType, item, categories) => {
    const key = `${org.id}:${itemType}:${item.id}`;
    setSavingCategoryKey(key);
    // Optimistic local update
    setAzureOrgs((prev) => prev.map((o) => {
      if (o.id !== org.id) return o;
      const listKey = itemType === 'repository' ? 'repositories' : 'projects';
      return {
        ...o,
        [listKey]: (o[listKey] || []).map((row) =>
          String(row.id) === String(item.id)
            ? { ...row, application_ids: categories.application_ids, service_ids: categories.service_ids }
            : row
        )
      };
    }));
    try {
      await api.put(`/settings/repositories/azure/organizations/${org.id}/categories`, {
        item_type: itemType,
        item_id: item.id,
        application_ids: categories.application_ids,
        service_ids: categories.service_ids
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save categories');
      await load();
    } finally {
      setSavingCategoryKey(null);
    }
  };

  const handleDeleteOrg = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/settings/repositories/azure/organizations/${deleteTarget.id}`);
      toast.success('Organization removed');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove organization');
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!credForm.token.trim() && !gitCreds.token_set) {
      toast.error('Enter a PAT, or configure AZURE_CLIENT_ID / SECRET from infra');
      return;
    }
    setSavingCreds(true);
    try {
      const payload = {
        provider: 'azure_devops',
        username: credForm.username.trim() || 'pat',
        organization: credForm.organization.trim()
      };
      if (credForm.token.trim()) payload.token = credForm.token.trim();
      const response = await api.put('/settings/repositories/credentials', payload);
      toast.success(response.data?.message || 'Credentials saved');
      setCredForm((f) => ({ ...f, token: '' }));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save credentials');
    } finally {
      setSavingCreds(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Repository Settings</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 760 }}>
            Connect cloud organizations to discover repositories and project boards.
            Azure DevOps is available now; GCP and AWS are reserved for later.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      {error && <SettingsErrorAlert error={error} onRetry={load} />}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={cloudTab}
          onChange={(_, v) => setCloudTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<CloudIcon />} iconPosition="start" label="Azure" />
          <Tab icon={<CloudIcon />} iconPosition="start" label="GCP" />
          <Tab icon={<CloudIcon />} iconPosition="start" label="AWS" />
        </Tabs>
      </Paper>

      {/* Azure */}
      <TabPanel value={cloudTab} index={0}>
        {/* Organizations first */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 2,
            border: '1px solid #90caf9',
            bgcolor: '#e3f2fd',
            backgroundImage: 'linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#0d47a1' }}>
                Azure DevOps organizations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                Connected orgs with repositories, all branches, and project boards — use this when choosing a repo/branch for a tenant.
              </Typography>
            </Box>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddOrgOpen(true)}
                sx={{ bgcolor: '#1565c0' }}
              >
                Add Azure DevOps Organization
              </Button>
            )}
          </Box>

          {azureOrgs.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.7)' }}>
              <Typography color="text.secondary">
                No organizations connected yet. Add an Azure DevOps organization to pull repositories, branches, and boards.
              </Typography>
            </Paper>
          )}

          {azureOrgs.map((org) => (
            <Accordion
              key={org.id}
              defaultExpanded={azureOrgs.length === 1}
              sx={{
                mb: 1.5,
                borderRadius: '12px !important',
                border: '1px solid #64b5f6',
                bgcolor: '#fff',
                '&:before': { display: 'none' },
                overflow: 'hidden'
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#bbdefb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%', pr: 1 }}>
                  <Typography fontWeight={700} sx={{ color: '#0d47a1' }}>{org.organization}</Typography>
                  <Chip size="small" color="primary" label={`${org.repositories?.length || 0} repos`} />
                  <Chip
                    size="small"
                    color="secondary"
                    label={`${(org.repositories || []).reduce((n, r) => n + (r.branches?.length || 0), 0)} branches`}
                  />
                  <Chip size="small" label={`${org.boards?.length || 0} boards`} variant="outlined" />
                  <Chip size="small" label={`${org.projects?.length || 0} projects`} variant="outlined" />
                  {org.last_synced_at && (
                    <Typography variant="caption" color="text.secondary">
                      Synced {new Date(org.last_synced_at).toLocaleString()}
                    </Typography>
                  )}
                  {org.sync_error && <Chip size="small" color="error" label="Sync error" />}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#fafafa' }}>
                {org.sync_error && (
                  <Alert severity="error" sx={{ mb: 2 }}>{org.sync_error}</Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {canEdit && (
                    <>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={syncingId === org.id ? <CircularProgress size={14} /> : <RefreshIcon />}
                        onClick={() => handleSync(org)}
                        disabled={syncingId === org.id}
                      >
                        Refresh repos, branches & boards
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={() => setDeleteTarget(org)}
                      >
                        Remove organization
                      </Button>
                    </>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 1 }}>
                    {org.org_url} · auth: {org.auth_mode || '—'} ({org.auth_source || '—'})
                  </Typography>
                </Box>

                <Paper
                  elevation={0}
                  sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #ffcc80', bgcolor: '#fff3e0' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#e65100' }}>
                    Projects
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Tag each project with one or more software applications / services (e.g. shared infra for erp4c and others).
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Project</TableCell>
                          <TableCell>State</TableCell>
                          <TableCell sx={{ minWidth: 280 }}>Apps / Services</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(org.projects || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body2" color="text.secondary">No projects found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {(org.projects || []).map((p) => (
                          <TableRow key={p.id} hover>
                            <TableCell sx={{ fontWeight: 700, color: '#e65100' }}>{p.name}</TableCell>
                            <TableCell>{p.state || '—'}</TableCell>
                            <TableCell>
                              <CatalogCategorySelect
                                applications={applications}
                                services={services}
                                applicationIds={p.application_ids || []}
                                serviceIds={p.service_ids || []}
                                disabled={!canEdit || savingCategoryKey === `${org.id}:project:${p.id}`}
                                onChange={(cats) => handleCategoryChange(org, 'project', p, cats)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #ce93d8', bgcolor: '#f3e5f5' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#6a1b9a' }}>
                    Repositories & branches
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Tag repositories with software apps/services (multi-select). Shared infra repos can apply to multiple products.
                    Use All Branches when deciding which branch to create for a tenant.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Project</TableCell>
                          <TableCell>Repository</TableCell>
                          <TableCell sx={{ minWidth: 260 }}>Apps / Services</TableCell>
                          <TableCell>Default branch</TableCell>
                          <TableCell sx={{ minWidth: 220 }}>All branches</TableCell>
                          <TableCell>URL</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(org.repositories || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Typography variant="body2" color="text.secondary">No repositories found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {(org.repositories || []).map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell>{r.project}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: '#4a148c' }}>{r.name}</TableCell>
                            <TableCell>
                              <CatalogCategorySelect
                                applications={applications}
                                services={services}
                                applicationIds={r.application_ids || []}
                                serviceIds={r.service_ids || []}
                                disabled={!canEdit || savingCategoryKey === `${org.id}:repository:${r.id}`}
                                onChange={(cats) => handleCategoryChange(org, 'repository', r, cats)}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip size="small" color="primary" label={r.default_branch || '—'} />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 360 }}>
                                {(r.branches || []).length === 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    No branches listed — click Refresh
                                  </Typography>
                                )}
                                {(r.branches || []).map((b) => (
                                  <Chip
                                    key={`${r.id}-${b}`}
                                    size="small"
                                    variant={b === r.default_branch ? 'filled' : 'outlined'}
                                    color={b === r.default_branch ? 'primary' : 'default'}
                                    label={b}
                                    sx={{ fontFamily: 'monospace', fontSize: 11 }}
                                  />
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 180 }}>
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {r.url}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{ p: 1.5, borderRadius: 2, border: '1px solid #80cbc4', bgcolor: '#e0f2f1' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: '#00695c' }}>
                    Project boards
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Project</TableCell>
                          <TableCell>Team</TableCell>
                          <TableCell>Board</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(org.boards || []).length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body2" color="text.secondary">No boards found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                        {(org.boards || []).map((b) => (
                          <TableRow key={`${b.project}-${b.team}-${b.id}`} hover>
                            <TableCell>{b.project}</TableCell>
                            <TableCell>{b.team}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{b.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>

        {/* Connectivity below, collapsed by default */}
        <Accordion
          defaultExpanded={false}
          sx={{
            mb: 2,
            borderRadius: '12px !important',
            border: '1px solid',
            borderColor: azureConnection.connected ? '#a5d6a7' : '#ffe082',
            bgcolor: azureConnection.connected ? '#e8f5e9' : '#fffde7',
            '&:before': { display: 'none' },
            overflow: 'hidden'
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', width: '100%' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: azureConnection.connected ? '#1b5e20' : '#f57f17' }}>
                Azure DevOps connectivity
              </Typography>
              <Chip
                size="small"
                color={azureConnection.connected ? 'success' : 'warning'}
                label={azureConnection.connected
                  ? `${azureConnection.mode === 'service_principal' ? 'Service principal' : 'PAT'} connected`
                  : 'Not connected'}
              />
              <Typography variant="caption" color="text.secondary">
                Expand only if you need to check auth or save a PAT fallback
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: 720 }}>
              Preferred: app registration from <strong>pilvi-infra-tf-config</strong> Secret Manager
              (`AZURE_TENANT_ID`, `AZURE_CLIENT_ID`, `AZURE_CLIENT_SECRET`).
              Optional fallback: PAT in Kubernetes Secret.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              {azureConnection.message || azureConnection.note || 'Configure credentials to add organizations.'}
            </Alert>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Optional PAT fallback
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Username"
                  value={credForm.username}
                  disabled={!canEdit}
                  onChange={(e) => setCredForm((f) => ({ ...f, username: e.target.value }))}
                  helperText="Azure DevOps: any non-empty value (often pat)"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="PAT / Token"
                  autoComplete="new-password"
                  placeholder={gitCreds.token_set ? '•••••••• (leave blank to keep)' : 'Azure DevOps PAT'}
                  value={credForm.token}
                  disabled={!canEdit}
                  onChange={(e) => setCredForm((f) => ({ ...f, token: e.target.value }))}
                />
              </Grid>
            </Grid>
            {canEdit && (
              <Box sx={{ mt: 1.5 }}>
                <Button variant="outlined" onClick={handleSaveCredentials} disabled={savingCreds}>
                  {savingCreds ? 'Saving…' : 'Save PAT to K8s Secret'}
                </Button>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </TabPanel>

      {/* GCP */}
      <TabPanel value={cloudTab} index={1}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>GCP</Typography>
          <Typography color="text.secondary">
            {gcp.message || 'GCP organization connectivity will be added in a future release.'}
          </Typography>
          <Chip sx={{ mt: 2 }} label="Coming soon" />
        </Paper>
      </TabPanel>

      {/* AWS */}
      <TabPanel value={cloudTab} index={2}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>AWS</Typography>
          <Typography color="text.secondary">
            {aws.message || 'AWS organization connectivity will be added in a future release.'}
          </Typography>
          <Chip sx={{ mt: 2 }} label="Coming soon" />
        </Paper>
      </TabPanel>

      {/* Add organization dialog */}
      <Dialog open={addOrgOpen} onClose={() => !addingOrg && setAddOrgOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add Azure DevOps Organization
          <IconButton size="small" onClick={() => setAddOrgOpen(false)} disabled={addingOrg}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Uses the infra service principal when available. On connect, TenantHub pulls all projects,
            git repositories, and project boards under the organization.
          </Alert>
          <TextField
            fullWidth
            autoFocus
            label="Organization name or URL"
            placeholder="pilvisystems  or  https://dev.azure.com/pilvisystems"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            helperText="Organization name only, or full https://dev.azure.com/{org} URL"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOrgOpen(false)} disabled={addingOrg}>Cancel</Button>
          <Button variant="contained" onClick={handleAddOrganization} disabled={addingOrg}>
            {addingOrg ? 'Connecting…' : 'Connect organization'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Remove organization</DialogTitle>
        <DialogContent>
          <Typography>
            Remove &quot;{deleteTarget?.organization}&quot; and its cached repositories/boards from TenantHub?
            This does not delete anything in Azure DevOps.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteOrg} disabled={deleting}>
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepositorySettings;
